import { Injectable } from '@angular/core';
import { init, browserTracingIntegration, setUser } from '@sentry/angular';
import { environment } from 'src/environments/environment';

interface SentryUserDetails {
  id: string;
  restaurantId?: string;
  restaurantName?: string;
  hqId?: string;
  hqName?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SentryService {
  private isInitialized = false;
  private lastUserDetailsUpdate = 0;
  private readonly UPDATE_THROTTLE_MS = 30000; // 30 seconds throttle
  private lastUserDetails: string | null = null;

  constructor() {}

  initSentry() {
    // Prevent multiple initializations
    if (this.isInitialized) {
      return;
    }

    try {
      init({
        dsn: environment.sentry.dsn,
        environment: environment.production ? 'production' : 'development',
        integrations: [
          browserTracingIntegration({
            // Disable automatic route instrumentation to reduce overhead
            instrumentNavigation: false,
            instrumentPageLoad: false,
          }),
        ],

        // Use more conservative sampling rates to prevent 429 errors
        tracesSampleRate: 0.1,

        // Control for which URLs trace propagation should be enabled
        tracePropagationTargets: [
          'localhost',
          /^https:\/\/partner-dev\.easyeat\.ai\//,
          /^https:\/\/partner-dev\.easyeat\.id\//,
          /^https:\/\/partner\.easyeat\.ai\//,
          /^https:\/\/partner\.easyeat\.id\//,
        ],

        // Disable session replay completely to reduce bandwidth
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0.1, // Only capture 10% of error sessions

        // Add beforeSend hook to filter out excessive requests
        beforeSend(event) {
          // Filter out network errors and other noise
          if (event.exception?.values?.[0]?.type === 'ChunkLoadError') {
            return null;
          }

          // Filter out 429 errors from Sentry itself to prevent loops
          if (event.request?.url?.includes('sentry.io')) {
            return null;
          }

          return event;
        },

        // Reduce initial load impact
        autoSessionTracking: false,

        // Set transport options to handle rate limiting
        transport: undefined, // Use default transport with built-in retry logic
      });

      this.isInitialized = true;
    } catch (error) {
      console.warn('Failed to initialize Sentry:', error);
    }
  }

  // Method to set user details in Sentry with throttling
  setUserDetails(userDetails: SentryUserDetails | null): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      const now = Date.now();
      const userDetailsString = JSON.stringify(userDetails);

      // Throttle updates to prevent excessive API calls
      if (
        now - this.lastUserDetailsUpdate < this.UPDATE_THROTTLE_MS &&
        this.lastUserDetails === userDetailsString
      ) {
        return;
      }

      this.lastUserDetailsUpdate = now;
      this.lastUserDetails = userDetailsString;

      setUser(userDetails);
    } catch (error) {
      // Silently handle errors to prevent disrupting the application
      console.warn('Failed to set Sentry user details:', error);
    }
  }
}
